import { applyGraphicsPreview } from "./graphics-preview";
import { useMetadataEditingStore } from "../../stores/metadata-editing-store";
import { useScheduleStore } from "../../stores/schedule-store";
import Konva from "konva";
import { useCustomArtStore } from "../../stores/custom-art-store";
import { dispatchScheduleEvent } from "../../lib/schedule-history/dispatch";
import { useMetadataBuilderStore } from "../../stores/metadata-builder-store";

export function attachCustomArt(stage: Konva.Stage) {
  const background = new Konva.Layer();
  const controls = new Konva.Layer();
  const nodes = new Map<string, Konva.Image>();
  const transformer = new Konva.Transformer({
    nodes: [], keepRatio: true, flipEnabled: false,
    enabledAnchors: ["top-left", "top-right", "bottom-left", "bottom-right"],
  });
  controls.add(transformer);
  stage.add(background);
  background.moveToBottom();
  stage.add(controls);
  let showControls = true;
  let selectedId: string | null = null;

  const refreshSelection = () => {
    const node = selectedId ? nodes.get(selectedId) : undefined;
    const selectable = showControls && node?.visible();
    transformer.nodes(selectable && node ? [node] : []);
  };

  const onPointerDown = (event: Konva.KonvaEventObject<PointerEvent>) => {
    if (transformer.isAncestorOf(event.target)) return;
    const entry = [...nodes].find(([, node]) => node === event.target);
    showControls = !!entry;
    if (entry) useCustomArtStore.getState().selectLayer(entry[0]);
    refreshSelection();
  };
  stage.on("pointerdown", onPointerDown);

  const update = () => {
    const state = useCustomArtStore.getState();
    const builderActive = useMetadataBuilderStore.getState().active;
    const layers = builderActive ? [] : state.layers;
    if (selectedId !== state.selectedId) showControls = true;
    selectedId = state.selectedId;
    nodes.forEach((node, imageId) => {
      if (layers.some(layer => layer.imageId === imageId)) return;
      node.destroy();
      nodes.delete(imageId);
    });
    layers.forEach((layer, index) => {
      let node = nodes.get(layer.imageId);
      if (!node) {
        node = new Konva.Image({ image: layer.image, draggable: true });
        nodes.set(layer.imageId, node);
        background.add(node);
        const imageNode = node;
        imageNode.on("dragend transformend", () => {
          if (useMetadataBuilderStore.getState().active) return;
          dispatchScheduleEvent({
            type: "art/setTransform", imageId: layer.imageId,
            transform: {
              x: imageNode.x(), y: imageNode.y(), width: imageNode.width(), height: imageNode.height(),
              scaleX: imageNode.scaleX(), scaleY: imageNode.scaleY(), rotation: imageNode.rotation(),
            },
          });
        });
      }
      node.setAttrs({ image: layer.image, ...layer.transform, visible: layer.visible });
      node.zIndex(index);
    });
    applyGraphicsPreview(background);
    refreshSelection();
    transformer.forceUpdate();
  };
  update();
  const unsubscribe = useCustomArtStore.subscribe(() => {
    showControls = true;
    update();
  });
  const unsubscribeBuilder = useMetadataBuilderStore.subscribe((state, previous) => {
    if (state.active !== previous.active) update();
  });
  const unsubscribeView = useMetadataEditingStore.subscribe((state, previous) => {
    if (state.graphicsOpacity !== previous.graphicsOpacity || state.grayscale !== previous.grayscale) update();
  });
  const unsubscribeMode = useScheduleStore.subscribe((state, previous) => {
    if (state.developerMode !== previous.developerMode) update();
  });
  return () => {
    unsubscribe();
    unsubscribeBuilder();
    unsubscribeView();
    unsubscribeMode();
    nodes.forEach(node => node.off("dragend transformend"));
    stage.off("pointerdown", onPointerDown);
  };
}
